import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Scale, ArrowRight, CheckCircle2 } from "lucide-react";
import { logger } from "@/lib/logger";

/**
 * Compact rebalance-drift indicator for the dashboard.
 *
 * Fetches ``GET /rebalance/plan`` once and shows the largest deviation
 * between current and target allocation. Hidden entirely when the user
 * has no investments yet — the widget would just be noise then.
 */
export default function RebalanceDriftWidget() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/rebalance/plan")
      .then((r) => setPlan(r.data))
      .catch((e) => logger.warn("drift widget", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !plan || (plan.total_investments || 0) <= 0) return null;

  const rows = plan.rows || [];
  const drifts = rows.filter((r) => (r.target_pct || 0) > 0 || (r.current_pct || 0) > 0);
  if (drifts.length === 0) return null;

  const maxRow = drifts.reduce(
    (best, r) => (Math.abs(r.delta_pct || 0) > Math.abs(best.delta_pct || 0) ? r : best),
    drifts[0],
  );
  const maxDelta = Math.abs(maxRow.delta_pct || 0);
  const tolerance = plan.tolerance_pct || 0;
  const inBalance = maxDelta < tolerance;
  const tradeCount = (plan.trades || []).length;

  const typeLabel = {
    stock: "Acciones",
    crypto: "Cripto",
    fund: "Fondos",
    real_estate: "Inmueble",
    deposit: "Depósitos",
    other: "Otros",
  }[maxRow.type] || maxRow.type;

  return (
    <button
      type="button"
      onClick={() => navigate("/rebalance")}
      data-testid="drift-widget"
      className={`w-full text-left card-surface p-5 transition-colors ${
        inBalance
          ? "hover:border-[#1C7C54]/40"
          : "border border-[#D95D39]/25 bg-[#D95D39]/5 hover:bg-[#D95D39]/10"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          inBalance ? "bg-[#1C7C54]/10" : "bg-[#D95D39]/15"
        }`}>
          {inBalance
            ? <CheckCircle2 className="w-4 h-4 text-[#1C7C54]" />
            : <Scale className="w-4 h-4 text-[#D95D39]" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
            Rebalance
          </p>
          {inBalance ? (
            <h3 className="font-display text-lg font-semibold text-[#1C7C54]">
              Cartera en balance
            </h3>
          ) : (
            <h3 className="font-display text-lg font-semibold text-[#0A110E]">
              Desvío {maxDelta.toFixed(1)}pp · {typeLabel}
            </h3>
          )}
          <p className="text-xs text-[#5F7368] mt-0.5">
            {inBalance
              ? `Todas las clases dentro de ±${tolerance}pp`
              : `${tradeCount} operaci${tradeCount === 1 ? "ón" : "ones"} sugerida${tradeCount === 1 ? "" : "s"}`}
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-[#8A9992] shrink-0" />
      </div>

      {!inBalance && (
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-[#EBEBEA] overflow-hidden">
            <div
              className="h-full bg-[#D95D39] transition-all duration-500"
              style={{ width: `${Math.min(100, maxDelta * 5)}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-[#8A9992]">
            {maxRow.current_pct}% → {maxRow.target_pct}%
          </span>
        </div>
      )}
    </button>
  );
}
